import { useEffect, useState } from "react";
import { X, Plus, Trash2, ArrowUp, ArrowDown, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const inputCls = "flex-1 bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-[#D4AF37]";

const TemplateRow = ({ t, first, last, onChange, onMove, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(t.label);
  const save = () => { onChange({ ...t, label }); setEditing(false); };
  return (
    <li className={`flex items-center gap-2 border border-white/10 p-3 ${t.active ? "bg-[#0A0A0A]" : "bg-[#050505] opacity-60"}`} data-testid={`task-template-${t.id}`}>
      <button onClick={() => onChange({ ...t, active: !t.active })} title={t.active ? "Active — click to disable" : "Disabled — click to enable"}
        className={`w-9 h-5 p-0 flex-none rounded-full relative mr-2 transition-colors duration-200 ${t.active ? "bg-[#D4AF37]" : "bg-white/15"}`} data-testid={`task-template-toggle-${t.id}`}>
        <span className={`absolute left-0 top-0.5 w-4 h-4 rounded-full bg-black transition-transform duration-200 ${t.active ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </button>
      {editing ? (
        <>
          <input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className={inputCls} data-testid={`task-template-input-${t.id}`} />
          <button onClick={save} className="text-[#D4AF37] p-1" data-testid={`task-template-save-${t.id}`}><Check size={16} /></button>
        </>
      ) : (
        <span className="flex-1 text-sm text-white/85" data-testid={`task-template-label-${t.id}`}>{t.label}</span>
      )}
      {!editing && <button onClick={() => setEditing(true)} className="text-white/40 hover:text-[#D4AF37] p-1" data-testid={`task-template-edit-${t.id}`}><Pencil size={14} /></button>}
      <button disabled={first} onClick={() => onMove(-1)} className="text-white/40 hover:text-white p-1 disabled:opacity-20" data-testid={`task-template-up-${t.id}`}><ArrowUp size={14} /></button>
      <button disabled={last} onClick={() => onMove(1)} className="text-white/40 hover:text-white p-1 disabled:opacity-20" data-testid={`task-template-down-${t.id}`}><ArrowDown size={14} /></button>
      <button onClick={onDelete} className="text-white/40 hover:text-red-400 p-1" data-testid={`task-template-delete-${t.id}`}><Trash2 size={14} /></button>
    </li>
  );
};

export const TaskTemplatesModal = ({ onClose }) => {
  const [list, setList] = useState(null);
  const [draft, setDraft] = useState("");

  const load = () => api.get("/admin/onboarding-templates").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const put = (t) => api.put(`/admin/onboarding-templates/${t.id}`, { label: t.label, active: t.active, order: t.order });
  const change = async (t) => {
    if (t.label.trim().length < 2) { toast.error("Task name is too short"); return; }
    try { await put(t); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const move = async (i, dir) => {
    const a = list[i], b = list[i + dir];
    try { await Promise.all([put({ ...a, order: b.order }), put({ ...b, order: a.order })]); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete the template "${t.label}"? Tasks already created for players stay as they are.`)) return;
    try { await api.delete(`/admin/onboarding-templates/${t.id}`); toast.success("Template deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const add = async (e) => {
    e.preventDefault();
    if (draft.trim().length < 2) return;
    try { await api.post("/admin/onboarding-templates", { label: draft.trim(), active: true }); setDraft(""); toast.success("Template added"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 py-10 overflow-y-auto" onClick={onClose} data-testid="task-templates-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-2xl">
        <div className="flex justify-between items-start mb-2">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Immediate Tasks</p>
            <h2 className="font-heading text-xl font-black mt-1">Manage Task Templates</h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="task-templates-close"><X size={20} /></button>
        </div>
        <p className="text-white/40 text-xs mb-5">Active templates become tasks for every new player (including converted leads). Use <span className="text-[#D4AF37]">[Player Name]</span> to insert the player's name. Changes only affect players added from now on.</p>
        <ul className="space-y-2" data-testid="task-templates-list">
          {list === null && <p className="text-white/40 text-xs">Loading…</p>}
          {list?.map((t, i) => (
            <TemplateRow key={`${t.id}-${t.label}-${t.active}`} t={t} first={i === 0} last={i === list.length - 1}
              onChange={change} onMove={(d) => move(i, d)} onDelete={() => remove(t)} />
          ))}
        </ul>
        <form onSubmit={add} className="flex gap-2 mt-5">
          <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. Order Academy Kit for [Player Name]" className={inputCls} data-testid="task-template-new-input" />
          <button type="submit" disabled={draft.trim().length < 2} className="flex items-center gap-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 disabled:opacity-40" data-testid="task-template-add">
            <Plus size={14} /> Add
          </button>
        </form>
      </div>
    </div>
  );
};
